import React from "react";
import {
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { dateTime, money, relativeDate, surfaceSx } from "../center/ownerCenterUi";

const SALE_STATUS_LABELS = {
  draft: "Borrador",
  quoted: "Cotizada",
  accepted: "Aceptada",
  in_progress: "En curso",
  completed: "Completada",
  cancelled: "Cancelada",
};

const RECURRING_STATUS_LABELS = {
  active: "Activa",
  paused: "Pausada",
  ended: "Finalizada",
};

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function minutesLabel(value) {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!hours) return `${remainder} min`;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function MoneyTotals({ totals }) {
  const rows = Object.entries(totals || {});
  if (!rows.length) {
    return <Typography sx={{ py: 1.2, color: "text.secondary", fontSize: ".8rem" }}>Todavía no hay ventas o propuestas registradas para esta persona.</Typography>;
  }
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1 }}>
      {rows.map(([currency, row]) => (
        <Box key={currency} sx={{ p: 1.15, borderRadius: 2.2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.03)" }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".67rem", fontWeight: 900 }}>{currency}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: .8, mt: .55 }}>
            <Box><Typography sx={{ color: "text.secondary", fontSize: ".63rem" }}>Vendido</Typography><Typography sx={{ fontSize: ".78rem", fontWeight: 950 }}>{money(row.sold, currency)}</Typography></Box>
            <Box><Typography sx={{ color: "text.secondary", fontSize: ".63rem" }}>Cobrado</Typography><Typography sx={{ fontSize: ".78rem", fontWeight: 950 }}>{money(row.paid, currency)}</Typography></Box>
            <Box><Typography sx={{ color: "text.secondary", fontSize: ".63rem" }}>Pendiente</Typography><Typography sx={{ fontSize: ".78rem", fontWeight: 950 }}>{money(row.outstanding, currency)}</Typography></Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function OwnerClientCommercialPanel({ context, platformProjects = [] }) {
  if (!context) return null;
  const totals = context?.totalsByCurrency || {};
  const sales = context?.sales || [];
  const payments = context?.payments || [];
  const recurringPlans = context?.recurringPlans || [];
  const timeEntries = context?.timeEntries || [];
  const timeSummary = context?.timeSummary || {};
  const commitments = context?.commitments || [];
  const initiatives = context?.initiatives || [];

  const saleMap = new Map(sales.map((row) => [idOf(row), row]));
  const commitmentMap = new Map(commitments.map((row) => [idOf(row), row]));
  const initiativeMap = new Map(initiatives.map((row) => [idOf(row), row]));
  const projectMap = new Map(platformProjects.map((row) => [idOf(row), row]));

  return (
    <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
      <Typography sx={{ fontWeight: 1000 }}>Dinero y tiempo</Typography>
      <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>
        Ventas, cobros, mensualidades Owner y horas. La facturación del producto WebProfit se mantiene separada en su bloque.
      </Typography>

      <Stack spacing={1.5} sx={{ mt: 1.35 }}>
        <Box>
          <Stack direction="row" spacing={.6} alignItems="center" sx={{ mb: .8 }}>
            <PaymentsRoundedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 950, fontSize: ".86rem" }}>Relación comercial</Typography>
          </Stack>
          <MoneyTotals totals={totals} />
          {sales.length ? (
            <>
              <Divider sx={{ my: 1.15 }} />
              <Typography sx={{ color: "text.secondary", fontSize: ".67rem", fontWeight: 900 }}>VENTAS / TRABAJOS RECIENTES</Typography>
              <Stack>
                {sales.slice(0, 10).map((sale) => (
                  <Box key={idOf(sale)} sx={{ py: .85, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.06)", "&:last-of-type": { borderBottom: 0 } }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.8}>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap alignItems="center">
                          <Typography sx={{ fontSize: ".82rem", fontWeight: 950 }}>{sale.title}</Typography>
                          <Chip size="small" variant="outlined" label={SALE_STATUS_LABELS[sale.status] || "Sin estado"} sx={{ height: 20, fontSize: ".62rem", fontWeight: 850 }} />
                        </Stack>
                        {sale.expectedAt ? <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".68rem" }}>Esperada {dateTime(sale.expectedAt)}</Typography> : null}
                      </Box>
                      <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontSize: ".72rem" }}>Total <strong>{money(sale.total, sale.currency)}</strong></Typography>
                        <Typography sx={{ fontSize: ".72rem" }}>Cobrado <strong>{money(sale.paid, sale.currency)}</strong></Typography>
                        <Typography sx={{ fontSize: ".72rem" }}>Pendiente <strong>{money(sale.outstanding, sale.currency)}</strong></Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </>
          ) : null}
          {payments.length ? (
            <>
              <Divider sx={{ my: 1.15 }} />
              <Typography sx={{ color: "text.secondary", fontSize: ".67rem", fontWeight: 900 }}>COBROS RECIENTES</Typography>
              <Stack>
                {payments.slice(0, 8).map((payment) => {
                  const sale = saleMap.get(idOf(payment.saleId));
                  return (
                    <Stack key={idOf(payment)} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.5} sx={{ py: .65 }}>
                      <Box>
                        <Typography sx={{ fontSize: ".78rem", fontWeight: 900 }}>{sale?.title || "Cobro registrado"}</Typography>
                        <Typography sx={{ color: "text.secondary", fontSize: ".67rem" }}>{dateTime(payment.occurredAt)}{payment.note ? ` · ${payment.note}` : ""}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: ".78rem", fontWeight: 950 }}>{money(payment.amount, payment.currency)}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </>
          ) : null}
        </Box>

        <Divider />

        <Box>
          <Stack direction="row" spacing={.6} alignItems="center">
            <RepeatRoundedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 950, fontSize: ".86rem" }}>Mensualidades Owner</Typography>
          </Stack>
          <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".7rem" }}>Son compromisos comerciales del Owner; no son el billing de WebProfit.</Typography>
          {recurringPlans.length ? (
            <Stack sx={{ mt: .7 }}>
              {recurringPlans.slice(0, 10).map((plan) => (
                <Box key={idOf(plan)} sx={{ py: .8, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.06)", "&:last-of-type": { borderBottom: 0 } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.7}>
                    <Box>
                      <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap alignItems="center">
                        <Typography sx={{ fontSize: ".8rem", fontWeight: 950 }}>{plan.name}</Typography>
                        <Chip size="small" variant="outlined" label={RECURRING_STATUS_LABELS[plan.effectiveStatus] || "Sin estado"} sx={{ height: 20, fontSize: ".62rem", fontWeight: 850 }} />
                        {plan.dueState === "overdue" ? <Chip size="small" color="error" label="Vencida" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : null}
                      </Stack>
                      <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".68rem" }}>
                        {plan.nextDueAt ? `Próximo: ${relativeDate(plan.nextDueAt)} · ${dateTime(plan.nextDueAt)}` : "Sin próximo vencimiento"}
                        {plan.endDate ? ` · termina ${dateTime(plan.endDate)}` : ""}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: ".8rem", fontWeight: 950 }}>{money(plan.projectedAmount, plan.currency)}</Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : <Typography sx={{ py: 1, color: "text.secondary", fontSize: ".8rem" }}>Sin mensualidad Owner vinculada.</Typography>}
        </Box>

        <Divider />

        <Box>
          <Stack direction="row" spacing={.6} alignItems="center">
            <AccessTimeRoundedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 950, fontSize: ".86rem" }}>Horas</Typography>
          </Stack>
          <Stack direction="row" spacing={2.2} sx={{ mt: .65 }} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: ".78rem" }}>Total <strong>{Number(timeSummary.totalMinutes || 0) ? minutesLabel(timeSummary.totalMinutes) : "sin horas registradas"}</strong></Typography>
            <Typography sx={{ fontSize: ".78rem" }}>Últimos 30 días <strong>{Number(timeSummary.last30DaysMinutes || 0) ? minutesLabel(timeSummary.last30DaysMinutes) : "sin horas registradas"}</strong></Typography>
          </Stack>
          {timeEntries.length ? (
            <Stack sx={{ mt: .65 }}>
              {timeEntries.slice(0, 10).map((entry) => {
                const commitment = commitmentMap.get(idOf(entry.commitmentId));
                const initiative = initiativeMap.get(idOf(entry.initiativeId));
                const project = projectMap.get(idOf(entry.webProfitProjectId));
                return (
                  <Box key={idOf(entry)} sx={{ py: .72, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.06)", "&:last-of-type": { borderBottom: 0 } }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.55}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: ".78rem", fontWeight: 900 }}>{commitment?.title || "Actividad"}</Typography>
                        <Typography sx={{ mt: .18, color: "text.secondary", fontSize: ".67rem" }}>
                          {dateTime(entry.occurredAt)}
                          {initiative ? ` · Frente ${initiative.name}` : ""}
                          {project ? ` · WP ${project.name}` : entry.webProfitProjectId ? " · WP relacionado" : ""}
                        </Typography>
                        {entry.note ? <Typography sx={{ mt: .18, color: "text.secondary", fontSize: ".7rem", whiteSpace: "pre-wrap" }}>{entry.note}</Typography> : null}
                      </Box>
                      <Typography sx={{ fontSize: ".78rem", fontWeight: 950 }}>{minutesLabel(entry.minutes)}</Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : <Typography sx={{ py: 1, color: "text.secondary", fontSize: ".8rem" }}>Sin horas registradas. Para cargar tiempo, primero creá o elegí una actividad del cliente.</Typography>}
        </Box>
      </Stack>
    </Box>
  );
}
